import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export function CvDialog({ candidateId, candidateName, onClose }: { candidateId: number; candidateName: string; onClose: () => void }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [mime, setMime] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let created: string | null = null;
    api.fetchResumeFile(candidateId)
      .then(({ blob, mime: m }) => {
        if (!active) return;
        created = URL.createObjectURL(blob);
        setBlobUrl(created);
        setMime(m);
      })
      .catch((e: Error) => { if (active) setError(e.message || "Failed to load CV"); })
      .finally(() => { if (active) setLoading(false); });
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [candidateId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="flex h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-card bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-surface-secondary px-5 py-3">
          <p className="text-sm font-semibold text-near-black">{candidateName ? `${candidateName} — CV / Resume` : "CV / Resume"}</p>
          <div className="flex items-center gap-2">
            {blobUrl && (
              <a href={blobUrl} download={`CV_${(candidateName || "resume").replace(/\s+/g, "_")}.pdf`} className="flex items-center gap-1 rounded-btn px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black">
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download
              </a>
            )}
            <button onClick={onClose} className="rounded-btn p-1.5 text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1">
          {loading && <p className="flex h-full items-center justify-center text-sm text-text-secondary">Loading…</p>}
          {error && <p className="flex h-full items-center justify-center text-sm text-error-text">{error}</p>}
          {blobUrl && (mime?.includes("pdf") ? (
            <iframe src={blobUrl} className="h-full w-full border-0" title="Resume" />
          ) : (
            <div className="flex h-full items-center justify-center overflow-auto bg-gray-50 p-4">
              <img src={blobUrl} alt="Resume" className="max-h-full max-w-full object-contain" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
